<template>
  <VCard flat :border="border" :color="color" class="w-100" data-testid="action-table-page">
    <VCardTitle
      v-if="title || $slots['header-title'] || $slots['header-right']"
      data-testid="card-title"
    >
      <slot name="header-title">
        <slot name="header-title-before"></slot>
        <p class="mb-0 card-title" data-testid="title-text">
          {{ title }}
        </p>
        <slot name="header-title-after"></slot>
      </slot>
      <VSpacer />
      <slot name="header-right"></slot>
    </VCardTitle>
    <VCardSubtitle>
      <slot name="subheader"></slot>
    </VCardSubtitle>
    <VCardText>
      <VRow>
        <slot name="before-table"></slot>
        <slot name="divider"></slot>
        <VCol v-bind="contentColsAttr">
          <slot name="listerContent">
            <VCol class="py-0">
              <slot name="subheader">
                <div class="w-100 d-flex justify-space-between flex-wrap">
                  <slot name="subheader-left"><div></div></slot>
                  <slot name="subheader-right">
                    <div v-if="filterAsMenu && filters.length" class="pr-2">
                      <VMenu
                        v-if="!display.mobile.value"
                        :close-on-content-click="false"
                        width="500"
                        location="bottom center"
                        data-testid="filter-menu"
                      >
                        <template #activator="{ props: elementProps }">
                          <SocioBtn
                            prepend-icon="mdi-filter-variant"
                            variant="outlined"
                            :text="t('filter')"
                            class="mt-3"
                            v-bind="elementProps"
                            data-testid="filter-button"
                          />
                        </template>
                        <VContainer
                          fluid
                          class="filters-container pa-4"
                          data-testid="filters-container"
                        >
                          <span class="filters-container-title px-3" data-testid="filters-title">
                            {{ t("filter") }}
                          </span>
                          <div
                            class="filters-container-body pt-1 pt-md-2 px-3"
                            data-testid="filters-body"
                          >
                            <VCol>
                              <VRow v-for="(filter, idx) in filters" :key="idx">
                                <slot :name="`filter.${filter.key}`" :form="formFilter">
                                  <SocioCombobox
                                    v-if="filter.type === 'choice'"
                                    v-model="formFilter[filter.key]"
                                    class="mb-2"
                                    clearable
                                    :density="display.mobile.value ? 'compact' : undefined"
                                    :items="filter.options"
                                    :label="filter.label"
                                    item-title="text"
                                    item-value="value"
                                    :return-object="false"
                                    variant="outlined"
                                    :data-testid="'filter-' + filter.key"
                                  />
                                  <SocioTextField
                                    v-else
                                    v-model="formFilter[filter.key]"
                                    class="mb-2"
                                    clearable
                                    :density="display.mobile.value ? 'compact' : undefined"
                                    :label="filter.label"
                                    v-bind="filter.attrs && filter.attrs(formFilter)"
                                    variant="outlined"
                                    :data-testid="'filter-' + filter.key"
                                  />
                                </slot>
                              </VRow>
                            </VCol>
                          </div>
                          <div class="filters-container-footer mt-6 mb-3 px-1">
                            <SocioBtn
                              variant="outlined"
                              :disabled="!filtersLength"
                              :text="t('clearFilters')"
                              :class="{
                                'btn--filter_secondary': !filtersLength,
                              }"
                              data-testid="clear-filters-button"
                              @click="clearFilters"
                            />
                          </div>
                        </VContainer>
                      </VMenu>
                      <template v-else>
                        <SocioBtn
                          prepend-icon="mdi-filter-variant"
                          variant="outlined"
                          :text="t('filter')"
                          class="mt-3"
                          data-testid="filter-button-mobile"
                          @click="showFiltersDialog = true"
                        />
                        <SocioDialog
                          v-model="showFiltersDialog"
                          fullscreen
                          :scrim="false"
                          transition="dialog-bottom-transition"
                          data-testid="filters-dialog"
                        >
                          <VCard class="d-flex flex-column" data-testid="filters-dialog-card">
                            <VCardTitle
                              class="d-flex justify-space-between px-6"
                              data-testid="filters-dialog-title"
                            >
                              {{ t("filter") }}

                              <SocioIconBtn
                                icon="mdi-close"
                                size="24"
                                data-testid="close-filters-dialog-button"
                                @click="showFiltersDialog = false"
                              />
                            </VCardTitle>
                            <VCardText class="pa-6 h-8" data-testid="filters-dialog-content">
                              <VRow>
                                <VCol v-for="(filter, idx) in filters" :key="idx" cols="12">
                                  <slot :name="`filter.${filter.key}`" :form="formFilter">
                                    <SocioTextField
                                      v-model="formFilter[filter.key]"
                                      clearable
                                      :density="display.mobile.value ? 'compact' : undefined"
                                      :label="filter.label"
                                      v-bind="filter.attrs && filter.attrs(formFilter)"
                                      variant="outlined"
                                      :data-testid="'filter-mobile-' + filter.key"
                                    />
                                  </slot>
                                </VCol>
                              </VRow>
                            </VCardText>
                            <VCardActions class="px-3 mt-auto">
                              <VSpacer></VSpacer>
                              <SocioBtn
                                variant="outlined"
                                :disabled="!filtersLength"
                                :text="t('clearFilters')"
                                :class="{
                                  'btn--filter_secondary': !filtersLength,
                                }"
                                data-testid="clear-filters-button-mobile"
                                @click="clearFilters"
                              />
                            </VCardActions>
                          </VCard>
                        </SocioDialog>
                      </template>
                    </div>
                  </slot>
                </div>
              </slot>
              <VRow>
                <VCol class="mt-4">
                  <component
                    :is="serverMode ? 'SocioDataTableServer' : 'SocioDataTable'"
                    v-if="showDataTable"
                    v-model:options="options"
                    :headers="headers"
                    :items="displayedItems"
                    :no-data-text="noDataText || t('noData')"
                    :loading="loading"
                    :items-length="serverMode ? itemsCount : displayedItemsCount"
                    :show-select="showSelect"
                    :single-select="singleSelect"
                    :item-value="(item) => item"
                    item-key="uuid"
                    v-bind="$attrs"
                    :mobile-breakpoint="0"
                    data-testid="data-table"
                    @click:row="emit('click:row', $event)"
                  >
                    <template #loading>
                      <slot name="loading"></slot>
                    </template>
                    <template v-if="showSelect" #[`header.data-table-select`]>
                      <div class="custom-table-select">
                        <VCheckbox
                          :model-value="selectAll"
                          color="#FCB902"
                          :false-icon="'mdi-checkbox-blank-outline'"
                          :true-icon="'mdi-checkbox-marked'"
                          data-testid="select-all-checkbox"
                          @update:model-value="toggleSelectAll"
                        />
                      </div>
                    </template>

                    <template v-if="showSelect" #[`item.data-table-select`]="{ item }">
                      <div class="custom-table-select">
                        <VCheckbox
                          :model-value="isSelected(item)"
                          color="#FCB902"
                          :false-icon="
                            singleSelect
                              ? 'mdi-checkbox-blank-circle-outline'
                              : 'mdi-checkbox-blank-outline'
                          "
                          :true-icon="
                            singleSelect ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-marked'
                          "
                          :data-testid="'select-item-' + item.uuid"
                          @update:model-value="(value) => handleSelection(value, item)"
                        />
                      </div>
                    </template>

                    <template
                      v-for="(header, idx) in headers"
                      #[`item.${header.value}`]="{ item }"
                      :key="idx"
                    >
                      <div :style="header.style && header.style(item)">
                        <slot :name="`item.${header.value}`" :item="item">
                          <template v-if="header.render">
                            {{ header.render(item) }}
                          </template>
                          <template v-else>
                            {{ item[header.value] }}
                          </template>
                        </slot>
                      </div>
                    </template>

                    <template v-for="(_, slot) in $slots" #[slot]="{ item }">
                      <slot :name="slot" :item="item" />
                    </template>
                  </component>
                </VCol>
              </VRow>
              <slot name="after-table"></slot>
            </VCol>
          </slot>
        </VCol>
        <slot name="other" :filter-form="formFilter" :options="options" />
      </VRow>
    </VCardText>
  </VCard>
</template>

<script setup>
import { ref, watch, computed, onMounted, useSlots } from "vue";
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";
import messages from "@/locales/global/socio-lister-page.json";
import { debounce } from "lodash";

const props = defineProps({
  title: {
    type: String,
    default: "",
  },
  showDataTable: {
    type: Boolean,
    default: true,
  },
  items: {
    type: Array,
    required: true,
  },
  itemsCount: {
    type: Number,
    required: true,
  },
  noDataText: {
    type: String,
    default: "",
  },
  headers: {
    type: Array,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  showSelect: {
    type: Boolean,
    default: false,
  },
  singleSelect: {
    type: Boolean,
    default: true,
  },
  filters: {
    type: Array,
    default: () => [],
  },
  debounceDelay: {
    type: Number,
    default: 350,
  },
  itemsPerPage: {
    type: Array,
    default: () => [10, 20, 30],
  },
  filterAsMenu: {
    type: Boolean,
    default: false,
  },
  optionalFooterProps: {
    type: Object,
    default: () => ({}),
  },
  filtersNbrCorrection: {
    type: Number,
    default: 0,
  },
  outlined: {
    type: Boolean,
    default: false,
  },
  color: {
    type: String,
    default: "#FFF",
  },
  border: {
    type: Boolean,
    default: false,
  },
  serverMode: {
    type: Boolean,
    default: false,
  },
  selected: {
    type: Array,
    default: () => [],
  },
  localFilterMode: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["select", "click:row", "fetchDatas"]);
const selectedData = defineModel({
  type: Array,
  default: () => [],
});

const { t, mergeLocaleMessage } = useI18n();
const display = useDisplay();

mergeLocaleMessage("fr", messages.fr);
mergeLocaleMessage("en", messages.en);
mergeLocaleMessage("nl", messages.nl);

const slots = useSlots();

const contentColsAttr = computed(() => {
  return slots["before-table"] ? { class: "pa-0" } : { cols: "12", class: "pa-0" };
});

const options = ref({
  itemsPerPage: 10,
  page: 1,
});
const showFiltersDialog = ref(false);

const formFilter = ref(
  Object.fromEntries(props.filters.map((filter) => [filter.key, filter.defaultValue || ""])),
);

const activatedFilters = ref([]);

const selectedItems = computed({
  get: () => selectedData.value,
  set: (newSelection) => {
    selectedData.value = newSelection;
    emit("select", newSelection);
  },
});

// Local filtering logic
const filteredItems = computed(() => {
  if (!props.localFilterMode) return props.items;

  return props.items.filter((item) =>
    Object.entries(formFilter.value).every(([filterKey, filterValue]) =>
      matchesFilter(item, filterKey, filterValue),
    ),
  );
});

const matchesFilter = (item, filterKey, filterValue) => {
  // Empty filters match all items
  if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) {
    return true;
  }

  const filterConfig = props.filters.find((f) => f.key === filterKey);
  if (!filterConfig) return true;

  const itemValue = getItemValue(item, filterKey);

  if (filterConfig.type === "choice") {
    return Array.isArray(filterValue) ? filterValue.includes(itemValue) : itemValue === filterValue;
  }

  // Default text filter
  if (itemValue == null) return false;
  return String(itemValue).toLowerCase().includes(String(filterValue).toLowerCase());
};

const getItemValue = (item, filterKey) => {
  const valueMap = {
    agencyName: () => item.agency?.name,
    "analysisType.code": () => item.analysisType?.code,
  };

  return valueMap[filterKey] ? valueMap[filterKey]() : item[filterKey];
};

const displayedItems = computed(() => {
  return props.localFilterMode ? filteredItems.value : props.items;
});

const displayedItemsCount = computed(() => {
  return props.localFilterMode ? filteredItems.value.length : props.itemsCount;
});

onMounted(() => {
  if (props.selected && props.selected.length > 0) {
    selectedData.value = [...props.selected];
  }
});
watch(
  () => props.selected,
  (newSelected) => {
    if (newSelected && Array.isArray(newSelected)) {
      const currentSelection = selectedData.value || [];
      const hasChanged =
        newSelected.length !== currentSelection.length ||
        newSelected.some((item) => !currentSelection.includes(item));
      if (hasChanged) {
        selectedData.value = [...newSelected];
      }
    }
  },
  { deep: true },
);

const updateActivatedFilters = () => {
  activatedFilters.value = Object.keys(formFilter.value).filter((key) => {
    const value = formFilter.value[key];
    if (["year", "amount"].includes(key)) {
      return !!value.value;
    } else if (Array.isArray(value)) {
      return value.length > 0;
    } else if (typeof value === "boolean") {
      return true;
    } else if (typeof value === "string") {
      return !isNaN(Number(value)) ? Number(value) !== 0 : value !== "";
    } else if (typeof value === "object") {
      return value !== null;
    }
    return !!value;
  });
};

const emitOptionsAndFilters = () => {
  updateActivatedFilters();

  const validFilters = Object.fromEntries(
    Object.entries(formFilter.value).filter(([_, value]) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      } else if (typeof value === "boolean") {
        return true;
      } else if (typeof value === "string") {
        return !isNaN(Number(value)) ? Number(value) !== 0 : value !== "";
      } else if (typeof value === "object") {
        return value !== null;
      }
      return !!value;
    }),
  );

  emit("fetchDatas", {
    filters: validFilters,
    ...options.value,
  });
};

const clearFilters = () => {
  activatedFilters.value = [];
  for (const key of Object.keys(formFilter.value)) {
    if (Array.isArray(formFilter.value[key])) {
      formFilter.value[key] = [];
    } else if (typeof formFilter.value[key] === "object") {
      formFilter.value[key] = null;
    } else {
      formFilter.value[key] = null;
    }
  }
  if (!props.localFilterMode) {
    emit("fetchDatas", {
      filters: formFilter.value,
      ...options.value,
    });
  }
};

const filtersLength = computed(() => {
  return activatedFilters.value.length - props.filtersNbrCorrection;
});
const selectAll = computed(() => {
  if (!props.showSelect || !displayedItems.value.length) return false;
  return displayedItems.value.every((item) => selectedItems.value.includes(item));
});

const isSelected = (item) => {
  return selectedItems.value.includes(item);
};

const handleSelection = (isChecked, item) => {
  if (props.singleSelect) {
    selectedItems.value = isChecked ? [item] : [];
  } else {
    const currentSelection = [...selectedItems.value];
    if (isChecked) {
      if (!currentSelection.includes(item)) {
        currentSelection.push(item);
      }
    } else {
      const index = currentSelection.indexOf(item);
      if (index > -1) {
        currentSelection.splice(index, 1);
      }
    }
    selectedItems.value = currentSelection;
  }
};

const toggleSelectAll = (selectAllValue) => {
  if (props.singleSelect) return;
  if (selectAllValue) {
    selectedItems.value = [...displayedItems.value];
  } else {
    selectedItems.value = [];
  }
};

const debouncedEmit = debounce(emitOptionsAndFilters, props.debounceDelay);

watch(options, (newValue, oldValue) => {
  if (newValue === oldValue) {
    return;
  }
  if (props.serverMode) {
    debouncedEmit();
  }
});

watch(
  formFilter,
  () => {
    if (props.localFilterMode) {
      updateActivatedFilters();
    } else {
      debouncedEmit();
    }
  },
  { deep: true },
);
</script>

<style lang="scss" scoped>
.v-menu__content {
  &:has(.filters-container) {
    box-shadow: none !important;
    border: 1px solid #d4d5de !important;
  }
}
.filters-container {
  background-color: white !important;
  width: 500px;
  max-width: 500px;
  border: #e0e0e0 1px solid;

  .filters-container-title {
    font-size: 22px;
    font-weight: 500;
    margin-bottom: 16px;
    display: block;
  }

  .filters-container-body {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 24px;
    max-height: 50dvh;
    overflow-y: auto;
    align-items: flex-start;

    .col {
      display: flex;
      flex-direction: column;
      gap: 24px !important;
    }
  }

  .filters-container-footer {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }
}
.filters-nbr {
  color: #d80f0c;
  font-size: 12px;
  font-weight: 700;
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background-color: #fff0f0;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 2px solid #d80f0c;
  margin-left: 8px;
}
.border-md {
  border: 1px solid #d4d5de !important;
}
.card-title {
  color: $socotec-black_regular;
  font-size: 1.375rem;
  font-weight: 700;
  line-height: 2rem;
}
.theme--light.v-btn.v-btn--disabled.v-btn--has-bg.btn--filter_secondary {
  background-color: #fff !important;
  color: #c5c5c5 !important;
}

:deep(.v-input__details) {
  display: none;
}

.custom-table-select {
  margin-right: 16px;
}
</style>
<i18n>
  {
    "en": {
      "noData": "No data found",
      "fetchError": "Error while fetching data",
      "filter": "Filter",
      "clearFilters": "Erase all"
    },
    "fr": {
      "noData": "Aucune donnée n'a été trouvée",
      "fetchError": "Erreur lors de la récupération des données",
      "filter": "Filtrer",
      "clearFilters": "Tout effacer"
    }
  }
</i18n>
